import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { guardUnsavedBack } from '../../../lib/formGuard';
import { useSystemConfig } from '../../../contexts/SystemConfigContext';
import type { ClosingDetails } from '../../types/prf';

interface ClosingDetailsSectionProps {
  data: ClosingDetails;
  onSave: (data: ClosingDetails) => void;
  onBack: () => void;
}

export default function ClosingDetailsSection({ data, onSave, onBack }: ClosingDetailsSectionProps) {
  const { enabledResultOutcomes } = useSystemConfig();
  const [formData, setFormData] = useState<ClosingDetails>(data);

  const handleBackClick = () => guardUnsavedBack(data, formData, onBack);

  const handleOutcomeChange = (outcomeId: string) => {
    const option = enabledResultOutcomes.find((o) => o.id === outcomeId);
    setFormData((prev) => ({
      ...prev,
      resultOutcome: outcomeId,
      resultOutcomeLabel: option?.label || outcomeId,
    }));
  };

  const handleSave = () => {
    if (!formData.resultOutcome?.trim()) return;
    onSave(formData);
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={handleBackClick} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">Closing Details</h2>
              <p className="text-xs text-muted-foreground">Actions taken and patient outcome</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Result / Outcome *
            </label>
            <select
              value={formData.resultOutcome || ''}
              onChange={(e) => handleOutcomeChange(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            >
              <option value="">Select outcome...</option>
              {enabledResultOutcomes.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Actions Taken
            </label>
            <textarea
              value={formData.actionsTaken || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, actionsTaken: e.target.value }))}
              rows={8}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Treatment given, advice provided, discharge details..."
            />
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSave}
          disabled={!formData.resultOutcome?.trim()}
          className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-muted disabled:text-muted-foreground text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
}
